/**
 * Trous du texte à trous (spécification §4, ADR 0008).
 *
 * Syntaxe stockée : `{{n::réponse}}`, où `n` est le numéro du trou. Le numéro identifie la
 * carte fille de façon stable (ADR 0005) : ajouter ou supprimer un trou ne change pas le
 * suivi FSRS des autres. Deux trous de même numéro sont masqués ensemble, sur la même carte.
 *
 * Un trou saisi sans numéro (`{{réponse}}`) reçoit le premier numéro libre à
 * l'enregistrement (`numeroterTrous`). La réponse peut contenir des accolades équilibrées
 * (LaTeX : `{{1::$\frac{a}{b}$}}`) ; un caractère précédé de `\` est ignoré.
 */

export interface Trou {
  /** Numéro du trou ; `null` s'il n'est pas encore numéroté. */
  numero: number | null;
  reponse: string;
  /** Position de `{{` dans le texte. */
  debut: number;
  /** Position qui suit `}}`. */
  fin: number;
}

const NUMERO = /^([1-9][0-9]{0,5})::/;

/** Position du `}}` fermant le trou ouvert juste avant `depart`, ou -1. */
export function trouverFermeture(texte: string, depart: number): number {
  let profondeur = 0;
  for (let i = depart; i < texte.length; i++) {
    const caractere = texte[i];
    if (caractere === '\\') {
      i += 1;
    } else if (caractere === '{') {
      profondeur += 1;
    } else if (caractere === '}') {
      if (profondeur > 0) profondeur -= 1;
      else if (texte[i + 1] === '}') return i;
    }
  }
  return -1;
}

/** Trous du texte, dans l'ordre d'apparition. */
export function analyserTrous(texte: string): Trou[] {
  const trous: Trou[] = [];
  let position = texte.indexOf('{{');
  while (position !== -1) {
    const fermeture = trouverFermeture(texte, position + 2);
    if (fermeture === -1) break;
    const interieur = texte.slice(position + 2, fermeture);
    const numero = NUMERO.exec(interieur);
    trous.push({
      numero: numero ? Number(numero[1]) : null,
      reponse: numero ? interieur.slice(numero[0].length) : interieur,
      debut: position,
      fin: fermeture + 2,
    });
    position = texte.indexOf('{{', fermeture + 2);
  }
  return trous;
}

/** Numérote les trous qui n'ont pas de numéro, à la suite du plus grand numéro existant. */
export function numeroterTrous(texte: string): string {
  const trous = analyserTrous(texte);
  let suivant = Math.max(0, ...trous.map((trou) => trou.numero ?? 0)) + 1;
  let resultat = '';
  let curseur = 0;
  for (const trou of trous) {
    if (trou.numero !== null) continue;
    resultat += `${texte.slice(curseur, trou.debut)}{{${suivant}::${trou.reponse}}}`;
    curseur = trou.fin;
    suivant += 1;
  }
  return resultat + texte.slice(curseur);
}

/** Numéros distincts des trous, par ordre croissant (trous numérotés seulement). */
export function numerosDesTrous(texte: string): number[] {
  const numeros = analyserTrous(texte)
    .map((trou) => trou.numero)
    .filter((numero): numero is number => numero !== null);
  return [...new Set(numeros)].sort((a, b) => a - b);
}
